import React, { useState } from 'react';
import { getGradeLabel, GRADES, STUDENT_STATUSES, GENDERS } from '../../api/studentPortalApi';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 24px 32px;
  border-bottom: 2px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  h2 {
    margin: 0 0 8px 0;
    font-size: 24px;
    font-weight: bold;
  }

  p {
    margin: 0;
    opacity: 0.9;
    font-size: 14px;
  }
`;

const ModalBody = styled.div`
  padding: 32px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const ModalFooter = styled.div`
  padding: 20px 32px;
  border-top: 2px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #111827;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoItemFull = styled(InfoItem)`
  grid-column: 1 / -1;
`;

const InfoLabel = styled.div`
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
`;

const InfoValue = styled.div`
  color: #111827;
  font-size: 15px;
  font-weight: 500;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$color || '#e5e7eb'};
  color: white;
`;

const CampusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 6px;
  margin-bottom: 6px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  background: ${props => props.$selected ? '#3b82f6' : '#e5e7eb'};
  color: ${props => props.$selected ? 'white' : '#374151'};
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$clickable ? (props.$selected ? '#2563eb' : '#d1d5db') : props.$selected ? '#3b82f6' : '#e5e7eb'};
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &.primary {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
    }
  }

  &.secondary {
    background: white;
    color: #6b7280;
    border: 2px solid #e5e7eb;

    &:hover {
      background: #f9fafb;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const CampusSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const DeleteConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
`;

const DeleteConfirmDialog = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  h3 {
    margin: 0 0 16px;
    color: #dc2626;
    font-size: 18px;
  }

  p {
    margin: 0 0 24px;
    color: #4b5563;
    font-size: 14px;
    line-height: 1.5;
  }
`;

const DeleteConfirmButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const DeleteButton = styled(Button)`
  &.danger {
    background: #dc2626;
    color: white;
    border: none;

    &:hover {
      background: #b91c1c;
    }
  }
`;

const StudentDetailModal = ({
  student,
  onClose,
  onSave,
  onDelete,
  onUnlinkParent,
  onLinkParent,
  parentUsers = [],
  canEdit = true,
  campuses = [],
  canManageBillingVisibility = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedStudent, setEditedStudent] = useState({ ...student });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState('');
  const [parentSearchTerm, setParentSearchTerm] = useState('');

  if (!student) return null;

  // Get campus names for display
  const getCampusNames = (campusIds) => {
    if (!campusIds || campusIds.length === 0) return '-';
    return campusIds
      .map(id => campuses.find(c => c.id === id)?.name || '')
      .filter(Boolean)
      .join(', ');
  };

  // Toggle campus selection in edit mode
  const toggleCampus = (campusId) => {
    const currentCampuses = editedStudent.campuses || [];
    if (currentCampuses.includes(campusId)) {
      setEditedStudent({
        ...editedStudent,
        campuses: currentCampuses.filter(id => id !== campusId)
      });
    } else {
      setEditedStudent({
        ...editedStudent,
        campuses: [...currentCampuses, campusId]
      });
    }
  };

  // Toggle billing-visible campus in edit mode (system_admin only)
  const toggleBillingVisibleCampus = (campusId) => {
    const current = editedStudent.billingVisibleCampuses || [];
    if (current.includes(campusId)) {
      setEditedStudent({
        ...editedStudent,
        billingVisibleCampuses: current.filter(id => id !== campusId)
      });
    } else {
      setEditedStudent({
        ...editedStudent,
        billingVisibleCampuses: [...current, campusId]
      });
    }
  };

  const handleSave = () => {
    if (!editedStudent.campuses || editedStudent.campuses.length === 0) {
      alert('Please select at least one campus');
      return;
    }
    if (onSave) {
      onSave(editedStudent);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedStudent({ ...student });
    setIsEditing(false);
  };

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader style={{ position: 'relative' }}>
          <CloseButton onClick={onClose}>x</CloseButton>
          <h2>{isEditing ? 'Edit Student' : student.name}</h2>
          <p>
            {getGradeLabel(student.grade)}
            {student.schoolName && ` - ${student.schoolName}`}
          </p>
        </ModalHeader>

        <ModalBody>
          <Section>
            <SectionTitle>Student Information</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Student Name</InfoLabel>
                {isEditing ? (
                  <Input
                    value={editedStudent.name || ''}
                    onChange={(e) => setEditedStudent({ ...editedStudent, name: e.target.value })}
                    placeholder="Enter student name"
                  />
                ) : (
                  <InfoValue>{student.name}</InfoValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>Gender</InfoLabel>
                {isEditing ? (
                  <Select
                    value={editedStudent.gender || ''}
                    onChange={(e) => setEditedStudent({ ...editedStudent, gender: e.target.value })}
                  >
                    <option value="">Select Gender</option>
                    {GENDERS.map(g => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </Select>
                ) : (
                  <InfoValue>{student.gender || '-'}</InfoValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>Grade</InfoLabel>
                {isEditing ? (
                  <Select
                    value={editedStudent.grade || ''}
                    onChange={(e) => setEditedStudent({
                      ...editedStudent,
                      grade: e.target.value ? parseInt(e.target.value) : null
                    })}
                  >
                    <option value="">Select Grade</option>
                    {GRADES.map(g => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </Select>
                ) : (
                  <InfoValue>{getGradeLabel(student.grade) || '-'}</InfoValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>School Name</InfoLabel>
                {isEditing ? (
                  <Input
                    value={editedStudent.schoolName || ''}
                    onChange={(e) => setEditedStudent({ ...editedStudent, schoolName: e.target.value })}
                    placeholder="Enter school name"
                  />
                ) : (
                  <InfoValue>{student.schoolName || '-'}</InfoValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>Status</InfoLabel>
                {isEditing ? (
                  <Select
                    value={editedStudent.status || 'active'}
                    onChange={(e) => setEditedStudent({ ...editedStudent, status: e.target.value })}
                  >
                    {STUDENT_STATUSES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </Select>
                ) : (
                  <div>
                    <Badge $color={student.status === 'active' ? '#10b981' : '#ef4444'}>
                      {student.status === 'active' ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                )}
              </InfoItem>

              <InfoItemFull>
                <InfoLabel>Campuses *</InfoLabel>
                {isEditing ? (
                  <CampusSelector>
                    {campuses.map(campus => (
                      <CampusBadge
                        key={campus.id}
                        $selected={(editedStudent.campuses || []).includes(campus.id)}
                        $clickable
                        onClick={() => toggleCampus(campus.id)}
                      >
                        {campus.name}
                      </CampusBadge>
                    ))}
                  </CampusSelector>
                ) : (
                  <InfoValue title={getCampusNames(student.campuses)}>
                    {student.campuses && student.campuses.length > 0 ? (
                      <CampusSelector>
                        {student.campuses.map(campusId => {
                          const campus = campuses.find(c => c.id === campusId);
                          return campus ? (
                            <CampusBadge key={campusId} $selected>
                              {campus.name}
                            </CampusBadge>
                          ) : null;
                        })}
                      </CampusSelector>
                    ) : (
                      '-'
                    )}
                  </InfoValue>
                )}
              </InfoItemFull>

              {canManageBillingVisibility && (
                <InfoItemFull>
                  <InfoLabel>
                    Billing Visible Campuses&nbsp;
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#b45309',
                      background: '#fef3c7',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      textTransform: 'none',
                      marginLeft: '6px'
                    }}>
                      🔒 System Admin Only
                    </span>
                  </InfoLabel>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                    Leave empty for default visibility. When set, billing records are only visible to admin/manager of the chosen campuses; director is always blocked.
                  </div>
                  {isEditing ? (
                    <CampusSelector>
                      {campuses.map(campus => (
                        <CampusBadge
                          key={campus.id}
                          $selected={(editedStudent.billingVisibleCampuses || []).includes(campus.id)}
                          $clickable
                          onClick={() => toggleBillingVisibleCampus(campus.id)}
                        >
                          {campus.name}
                        </CampusBadge>
                      ))}
                    </CampusSelector>
                  ) : (
                    <InfoValue>
                      {student.billingVisibleCampuses && student.billingVisibleCampuses.length > 0 ? (
                        <CampusSelector>
                          {student.billingVisibleCampuses.map(campusId => {
                            const campus = campuses.find(c => c.id === campusId);
                            return campus ? (
                              <CampusBadge key={campusId} $selected>
                                {campus.name}
                              </CampusBadge>
                            ) : null;
                          })}
                        </CampusSelector>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>Default (no restriction)</span>
                      )}
                    </InfoValue>
                  )}
                </InfoItemFull>
              )}
            </InfoGrid>
          </Section>

          {/* Parent Account Link */}
          <Section>
            <SectionTitle>Parent Account / 家长账号</SectionTitle>
            {student.parentId ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: '#eff6ff',
                borderRadius: '8px',
                border: '1px solid #bfdbfe'
              }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#1e40af' }}>
                    {student.parentUserName || 'Unknown User'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    Linked parent account / 关联的家长账号
                  </div>
                </div>
                {!isEditing && canEdit && onUnlinkParent && (
                  <button
                    onClick={() => {
                      if (window.confirm('Unlink this parent account? The student will no longer be associated with this parent.\n\n确定取消关联此家长账号？该学生将不再与此家长关联。')) {
                        onUnlinkParent(student.id);
                      }
                    }}
                    style={{
                      padding: '6px 14px',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Unlink / 解除关联
                  </button>
                )}
              </div>
            ) : (
              <div>
                <div style={{
                  padding: '12px 16px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px dashed #d1d5db',
                  color: '#6b7280',
                  fontSize: '14px',
                  marginBottom: canEdit && onLinkParent ? '12px' : '0'
                }}>
                  No linked parent account / 未关联家长账号
                </div>
                {!isEditing && canEdit && onLinkParent && (
                  <div style={{
                    padding: '12px 16px',
                    background: '#f0fdf4',
                    borderRadius: '8px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{ fontSize: '13px', color: '#374151', marginBottom: '8px', fontWeight: '500' }}>
                      Link an existing parent account / 关联现有家长账号
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Input
                        placeholder="Search parent by name or email..."
                        value={parentSearchTerm}
                        onChange={(e) => setParentSearchTerm(e.target.value)}
                        style={{ marginBottom: '8px' }}
                      />
                      <Select
                        value={selectedParentId}
                        onChange={(e) => setSelectedParentId(e.target.value)}
                      >
                        <option value="">-- Select a parent / 选择家长 --</option>
                        {parentUsers
                          .filter(u => {
                            if (!parentSearchTerm) return true;
                            const term = parentSearchTerm.toLowerCase();
                            return (u.name || '').toLowerCase().includes(term) ||
                                   (u.email || '').toLowerCase().includes(term);
                          })
                          .map(u => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.email})
                            </option>
                          ))
                        }
                      </Select>
                    </div>
                    <button
                      onClick={() => {
                        if (!selectedParentId) {
                          alert('Please select a parent user.\n请选择一个家长用户。');
                          return;
                        }
                        const selectedParent = parentUsers.find(u => u.id === selectedParentId);
                        if (window.confirm(`Link parent "${selectedParent?.name}" to this student?\n\n确定将家长 "${selectedParent?.name}" 关联到此学生？`)) {
                          onLinkParent(student.id, selectedParentId);
                          setSelectedParentId('');
                          setParentSearchTerm('');
                        }
                      }}
                      disabled={!selectedParentId}
                      style={{
                        padding: '6px 14px',
                        background: selectedParentId ? '#16a34a' : '#d1d5db',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: selectedParentId ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Link Parent / 关联家长
                    </button>
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* Parent Information Section */}
          <Section>
            <SectionTitle>Parent Information</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Parent Name</InfoLabel>
                {isEditing ? (
                  <Input
                    value={editedStudent.parentName || ''}
                    onChange={(e) => setEditedStudent({ ...editedStudent, parentName: e.target.value })}
                    placeholder="Enter parent name"
                  />
                ) : (
                  <InfoValue>{student.parentName || '-'}</InfoValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>Parent Email</InfoLabel>
                {isEditing ? (
                  <Input
                    type="email"
                    value={editedStudent.parentEmail || ''}
                    onChange={(e) => setEditedStudent({ ...editedStudent, parentEmail: e.target.value })}
                    placeholder="Enter parent email"
                  />
                ) : (
                  <InfoValue>{student.parentEmail || '-'}</InfoValue>
                )}
              </InfoItem>

              <InfoItem>
                <InfoLabel>Parent Phone</InfoLabel>
                {isEditing ? (
                  <Input
                    type="tel"
                    value={editedStudent.parentPhone || ''}
                    onChange={(e) => setEditedStudent({ ...editedStudent, parentPhone: e.target.value })}
                    placeholder="Enter parent phone"
                  />
                ) : (
                  <InfoValue>{student.parentPhone || '-'}</InfoValue>
                )}
              </InfoItem>

              <InfoItemFull>
                <InfoLabel>Parent Address</InfoLabel>
                {isEditing ? (
                  <Input
                    value={editedStudent.parentAddress || ''}
                    onChange={(e) => setEditedStudent({ ...editedStudent, parentAddress: e.target.value })}
                    placeholder="Enter parent address"
                  />
                ) : (
                  <InfoValue>{student.parentAddress || '-'}</InfoValue>
                )}
              </InfoItemFull>
            </InfoGrid>
          </Section>

          {/* Additional Notes Section */}
          <Section>
            <SectionTitle>Additional Notes</SectionTitle>
            {isEditing ? (
              <TextArea
                value={editedStudent.additionalNotes || ''}
                onChange={(e) => setEditedStudent({ ...editedStudent, additionalNotes: e.target.value })}
                placeholder="Enter any additional notes..."
              />
            ) : (
              <InfoValue style={{ whiteSpace: 'pre-wrap' }}>
                {student.additionalNotes || 'No additional notes'}
              </InfoValue>
            )}
          </Section>

          {/* Timestamps */}
          <Section>
            <SectionTitle>Record Information</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Created At</InfoLabel>
                <InfoValue>
                  {student.createdAt ? new Date(student.createdAt).toLocaleString() : '-'}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Last Updated</InfoLabel>
                <InfoValue>
                  {student.updatedAt ? new Date(student.updatedAt).toLocaleString() : '-'}
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>
        </ModalBody>

        <ModalFooter>
          {!isEditing && canEdit && (
            <>
              <Button className="secondary" onClick={onClose}>Close</Button>
              {onDelete && (
                <DeleteButton
                  className="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete
                </DeleteButton>
              )}
              <Button className="primary" onClick={() => setIsEditing(true)}>Edit Student</Button>
            </>
          )}
          {isEditing && (
            <>
              <Button className="secondary" onClick={handleCancel}>Cancel</Button>
              <Button className="primary" onClick={handleSave}>Save Changes</Button>
            </>
          )}
          {!canEdit && (
            <Button className="secondary" onClick={onClose}>Close</Button>
          )}
        </ModalFooter>
      </ModalContainer>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <DeleteConfirmOverlay>
          <DeleteConfirmDialog>
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete student <strong>{student.name}</strong>?
              This action cannot be undone.
            </p>
            <DeleteConfirmButtons>
              <Button className="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <DeleteButton
                className="danger"
                onClick={() => {
                  if (onDelete) {
                    onDelete(student.id);
                  }
                  setShowDeleteConfirm(false);
                }}
              >
                Delete
              </DeleteButton>
            </DeleteConfirmButtons>
          </DeleteConfirmDialog>
        </DeleteConfirmOverlay>
      )}
    </ModalOverlay>
  );
};

export default StudentDetailModal;
